// src/contexts/UIContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';

const SessionContext = createContext();

// Accordion open/closed state is persisted so it survives graphic
// selection (which remounts the middle-column stack as it moves
// between bare and MaestroProvider-wrapped) and page reloads.
const ACCORDION_KEYS = {
  captionActionList: 'dashboard:accordion:captionActionList',
  captionData: 'dashboard:accordion:captionData',
  clockControl: 'dashboard:accordion:clockControl',
};
const readPersisted = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? fallback : raw === 'true';
  } catch {
    return fallback;
  }
};
const writePersisted = (key, value) => {
  try { localStorage.setItem(key, String(value)); } catch { /* ignore */ }
};

export const SessionProvider = ({ children }) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);
  const [isCaptionActionListOpen, setIsCaptionActionListOpen] = useState(
    () => readPersisted(ACCORDION_KEYS.captionActionList, true),
  );
  const [isCaptionDataOpen, setIsCaptionDataOpen] = useState(
    () => readPersisted(ACCORDION_KEYS.captionData, true),
  );
  const [isClockControlOpen, setIsClockControlOpen] = useState(
    () => readPersisted(ACCORDION_KEYS.clockControl, true),
  );
  const [selectedOrgId, setSelectedOrgId] = useState(() => {
    return localStorage.getItem('selectedOrgId') || "";
  });
  // OPUS-913: shared org list so the sidebar selector + every page read one
  // source instead of each fetching/holding its own.
  const [organizations, setOrganizations] = useState([]);

  // OPUS-1045: a bump counter the Settings page increments after add/edit/
  // delete/reorder of CTAs so the sidebar Quick Links re-fetch immediately
  // instead of staying stale until a full reload.
  const [ctasVersion, setCtasVersion] = useState(0);
  const bumpCtas = () => setCtasVersion((v) => v + 1);

  useEffect(() => {
    if (selectedOrgId) {
      localStorage.setItem('selectedOrgId', selectedOrgId);
    }
  }, [selectedOrgId]);

  const toggleDrawer = () => {
    setIsDrawerOpen(prev => !prev);
  };

  const toggleCaptionActionList = () => {
    setIsCaptionActionListOpen(prev => {
      const next = !prev;
      writePersisted(ACCORDION_KEYS.captionActionList, next);
      return next;
    });
  };

  const toggleCaptionData = () => {
    setIsCaptionDataOpen(prev => {
      const next = !prev;
      writePersisted(ACCORDION_KEYS.captionData, next);
      return next;
    });
  };

  const toggleClockControl = () => {
    setIsClockControlOpen(prev => {
      const next = !prev;
      writePersisted(ACCORDION_KEYS.clockControl, next);
      return next;
    });
  };

  const updateSelectedOrgId = (orgId) => {
    setSelectedOrgId(orgId);
  };

  return (
    <SessionContext.Provider value={{ isDrawerOpen, toggleDrawer, isCaptionActionListOpen, toggleCaptionActionList, isCaptionDataOpen, toggleCaptionData, isClockControlOpen, toggleClockControl,   selectedOrgId, updateSelectedOrgId, organizations, setOrganizations, ctasVersion, bumpCtas }}>
      {children}
    </SessionContext.Provider>
  );
};

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};
